import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import { crearProductoRapidoPos } from '../../services/crearProductoPos.service';
import {
  CATEGORIA_PESO_LABELS,
  CATEGORIA_PESO_VALORES,
  getPresetCodigoInterno,
  isCodigoInterno
} from '../../utils/codigoInterno';

const fieldClass =
  'nexo-field w-full rounded-xl border-2 border-slate-200 bg-white text-slate-900 ' +
  'focus:border-e0e3-accent focus:outline-none focus:ring-4 focus:ring-e0e3-accent/25';

const focusInput = (ref) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      ref.current?.focus?.();
      ref.current?.select?.();
    });
  });
};

export default function ProductoNoEncontradoModal({
  isOpen,
  onClose,
  codigoInicial = '',
  sucursalId,
  onCreado,
  onCrearYVender
}) {
  const nombreRef = useRef(null);
  const categoriaRef = useRef(null);
  const precioRef = useRef(null);
  const stockRef = useRef(null);
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [categoria, setCategoria] = useState('');
  const [precio, setPrecio] = useState('');
  const [vendidoPorPeso, setVendidoPorPeso] = useState(false);
  const [weightType, setWeightType] = useState('granel');
  const [cargarStock, setCargarStock] = useState(true);
  const [stock, setStock] = useState('10');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const preset = getPresetCodigoInterno(codigoInicial);
    setCodigo(String(codigoInicial || '').trim());
    setNombre(preset?.nombre || '');
    setCategoria(preset?.categoriaPeso ? CATEGORIA_PESO_LABELS[preset.categoriaPeso] || preset.categoriaPeso : '');
    setPrecio(preset?.precioPorKg ? String(preset.precioPorKg) : '');
    setVendidoPorPeso(false);
    setWeightType(preset?.categoriaPeso || 'granel');
    setCargarStock(true);
    setStock('10');
    setGuardando(false);
    setTimeout(() => focusInput(nombreRef), 100);
  }, [isOpen, codigoInicial]);

  const handleKeyDown = (e, nextRef, onSubmitLast) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (nextRef) focusInput(nextRef);
    else if (onSubmitLast) onSubmitLast('vender');
  };

  const guardar = async (accion) => {
    if (!nombre.trim()) {
      toast.error('Ingresá el nombre del producto');
      focusInput(nombreRef);
      return;
    }
    const precioNum = parseFloat(String(precio).replace(',', '.'));
    if (Number.isNaN(precioNum) || precioNum <= 0) {
      toast.error('Ingresá un precio válido');
      focusInput(precioRef);
      return;
    }
    if (vendidoPorPeso && !CATEGORIA_PESO_VALORES.includes(weightType)) {
      toast.error('Seleccioná el tipo de producto por peso');
      return;
    }
    if (!sucursalId) {
      toast.error('Seleccioná una sucursal');
      return;
    }

    setGuardando(true);
    try {
      const creado = await crearProductoRapidoPos({
        codigo: codigo.trim() || undefined,
        nombre: nombre.trim(),
        categoria: categoria.trim(),
        precioVenta: precioNum,
        cargarStock,
        stockInicial: cargarStock ? stock : 0,
        sucursalId,
        manualWeight: vendidoPorPeso,
        weightType: vendidoPorPeso ? weightType : null,
        precioPorKg: vendidoPorPeso ? precioNum : null,
        categoriaPeso: vendidoPorPeso ? weightType : null,
        codigoManual: isCodigoInterno(codigo) ? codigo.trim() : null
      });
      toast.success('Producto creado');
      if (accion === 'vender') {
        onCrearYVender?.(creado);
      } else {
        onCreado?.(creado);
      }
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'No se pudo crear el producto');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Producto no encontrado" size="md">
      <div
        className="flex max-h-[min(72vh,640px)] flex-col"
        data-producto-no-encontrado-modal
      >
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-y-contain pr-0.5">
          <div>
            <label className="text-sm font-semibold text-slate-700">Código</label>
            <input className={fieldClass} value={codigo} readOnly tabIndex={-1} />
            {isCodigoInterno(codigo) && (
              <span className="mt-1 inline-block rounded bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-900">
                Código interno
              </span>
            )}
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">Nombre</label>
            <input
              ref={nombreRef}
              className={fieldClass}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, categoriaRef)}
              disabled={guardando}
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">Categoría</label>
            <input
              ref={categoriaRef}
              className={fieldClass}
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, precioRef)}
              disabled={guardando}
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">
              {vendidoPorPeso ? 'Precio por kg ($)' : 'Precio ($)'}
            </label>
            <input
              ref={precioRef}
              type="number"
              min="0"
              step="0.01"
              className={fieldClass}
              value={precio}
              onChange={(e) => setPrecio(e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, cargarStock ? stockRef : null, () => guardar('vender'))}
              disabled={guardando}
            />
          </div>

          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-800">
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 accent-[#1e90ff]"
              checked={vendidoPorPeso}
              onChange={(e) => setVendidoPorPeso(e.target.checked)}
              disabled={guardando}
            />
            Producto vendido por peso
          </label>

          {vendidoPorPeso && (
            <div>
              <label className="text-sm font-semibold text-slate-700">Tipo de producto</label>
              <select
                className={fieldClass}
                value={weightType}
                onChange={(e) => setWeightType(e.target.value)}
                disabled={guardando}
              >
                {CATEGORIA_PESO_VALORES.map((v) => (
                  <option key={v} value={v}>
                    {CATEGORIA_PESO_LABELS[v] || v}
                  </option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 accent-[#1e90ff]"
              checked={cargarStock}
              onChange={(e) => setCargarStock(e.target.checked)}
              disabled={guardando}
            />
            Cargar stock inicial
          </label>
          {cargarStock && (
            <div>
              <label className="text-sm font-semibold text-slate-700">Stock</label>
              <input
                ref={stockRef}
                type="number"
                min="0"
                className={fieldClass}
                value={stock}
                onChange={(e) => setStock(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    guardar('vender');
                  }
                }}
                disabled={guardando}
              />
            </div>
          )}
        </div>

        <div className="nexo-modal-actions mt-3 shrink-0 border-t border-slate-200 bg-white pt-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Button
              color="primary"
              className="min-h-[48px] flex-1 !text-white"
              onClick={() => guardar('vender')}
              loading={guardando}
              disabled={guardando}
            >
              Crear y vender
            </Button>
            <Button
              color="secondary"
              className="min-h-[48px] flex-1 !bg-slate-700 !text-white hover:!bg-slate-800"
              onClick={() => guardar('crear')}
              disabled={guardando}
            >
              {guardando ? <Spinner size="sm" /> : 'Crear'}
            </Button>
            <Button
              color="secondary"
              outline
              className="min-h-[48px] !border-slate-400 !text-slate-800 hover:!bg-slate-100"
              onClick={onClose}
              disabled={guardando}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
